import { useEffect, useState } from 'react';
import {
    CpmButton,
    CpmCheckbox,
    CpmFilterDropdown,
    CpmIconButton,
} from '../../../components/design-system';
import type { CpmState } from '../../../types/centralProductMaster.types';
import {
    createCatalogReport,
    getCatalogDetails,
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
    listStates,
    updateCatalogReport,
} from '../../../services/reportsAdminService';
import {
    REPORT_CATALOG_CATEGORIES,
    type ActiveStatus,
    type CatalogDetails,
    type FormPhase,
    type ReportCatalogCategory,
    type ReportDataPoint,
    type ReportDateType,
    type ReportFileType,
    type ReportKind,
    type SavedTimeUnit,
} from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import { stateName } from '../helpers';
import InfoI from '../components/InfoI';
import InPlaceTagToggle from '../components/InPlaceTagToggle';
import {
    ReportsAlert,
    ReportsDialog,
    ReportsDialogBody,
    ReportsDialogFooter,
    ReportsDialogHeader,
    ReportsFieldLabel,
    ReportsHelp,
    ReportsInput,
    ReportsSegmented,
    ReportsTextarea,
    ReportsWait,
    reportsGrid,
    span,
} from '../components/ReportsDialog';
import GeneratorBindSelect from './GeneratorBindSelect';
import StoreBrandsForReportModal from './StoreBrandsForReportModal';
import TemplatesForReportModal from './TemplatesForReportModal';

interface ReportFormModalProps {
    mode: 'create' | 'edit';
    reportID?: string;
    onClose: () => void;
    onSaved: (info: { mode: 'create' | 'edit'; name: string }) => void;
}

interface FormState {
    name: string;
    description: string;
    categories: ReportCatalogCategory[];
    states: string[];
    allStates: boolean;
    kind: ReportKind;
    boundId: string | null;
    confirmNew: boolean;
    activeStatus: ActiveStatus;
    dateType: ReportDateType;
    fileVisible: boolean;
    fileSelectable: boolean;
    fileTypes: ReportFileType[];
    defaultType: ReportFileType;
    dataPoints: ReportDataPoint[];
    timeValue: number;
    timeUnit: SavedTimeUnit;
}

const EMPTY: FormState = {
    name: '',
    description: '',
    categories: [],
    states: [],
    allStates: true,
    kind: 'GENERATED',
    boundId: null,
    confirmNew: false,
    activeStatus: 'Active',
    dateType: 'Range Date',
    fileVisible: false,
    fileSelectable: false,
    fileTypes: ['Excel'],
    defaultType: 'Excel',
    dataPoints: [],
    timeValue: 0,
    timeUnit: 'minutes',
};

function fromDetails(row: CatalogDetails): FormState {
    return {
        name: row.name ?? '',
        description: row.description ?? '',
        categories: row.categories ?? [],
        states: row.states ?? [],
        allStates: Boolean(row.allStates),
        kind: row.kind,
        boundId: row.generatorBound ? row.reportID : null,
        confirmNew: false,
        activeStatus: row.activeStatus,
        dateType: row.config?.dateType ?? 'Range Date',
        fileVisible: row.config?.fileTypes?.isVisible ?? false,
        fileSelectable: row.config?.fileTypes?.isSelectable ?? false,
        fileTypes: row.config?.fileTypes?.types ?? ['Excel'],
        defaultType: row.config?.fileTypes?.defaultType ?? 'Excel',
        dataPoints: row.dataPoints ?? [],
        timeValue: row.config?.timeSaved?.value ?? 0,
        timeUnit: row.config?.timeSaved?.unit ?? 'minutes',
    };
}

const KIND_HELP: Record<ReportKind, string> = {
    GENERATED: 'Merchants can generate this once it is on for their store brand.',
    ALWAYS_ON:
        'Already available to every store brand. Cannot go in a template or the daily WhatsApp.',
    DEAD: 'No builder is linked. It cannot be added or turned on until one is.',
};

export default function ReportFormModal({
    mode,
    reportID,
    onClose,
    onSaved,
}: ReportFormModalProps) {
    const [phase, setPhase] = useState<FormPhase>(mode === 'edit' ? 'loading' : 'idle');
    const [form, setForm] = useState<FormState>(EMPTY);
    const [docId, setDocId] = useState<string | null>(null);
    const [savedId, setSavedId] = useState<string | null>(
        mode === 'edit' ? (reportID ?? null) : null
    );
    const [error, setError] = useState('');
    const [b9, setB9] = useState<'templates' | 'brands' | null>(null);
    const [packsIn, setPacksIn] = useState(0);
    const [brandsOn, setBrandsOn] = useState(0);
    const [justSaved, setJustSaved] = useState(false);
    const [stateDocs, setStateDocs] = useState<CpmState[]>([]);

    const locked = phase === 'saving';
    const generated = form.kind === 'GENERATED';
    const saved = Boolean(savedId);
    const canAssign =
        saved &&
        form.kind === 'GENERATED' &&
        form.activeStatus === 'Active' &&
        Boolean(form.boundId);
    const kindLocked = form.kind !== 'GENERATED';
    const patchForm = (partial: Partial<FormState>) => setForm((prev) => ({ ...prev, ...partial }));

    useEffect(() => {
        void listStates()
            .then(setStateDocs)
            .catch(() => undefined);
    }, []);

    const load = async () => {
        if (!reportID) return;
        setPhase('loading');
        setError('');
        try {
            const details = await getCatalogDetails(reportID);
            setForm(fromDetails(details));
            setDocId(details._id ?? null);
            setSavedId(details.reportID);
            setPacksIn(details.packsIn ?? 0);
            setBrandsOn(details.brandsOn ?? 0);
            setPhase('idle');
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('fetchError');
        }
    };

    useEffect(() => {
        if (mode === 'edit') void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, reportID]);

    const persist = async () => {
        if (!form.name.trim()) {
            setError('Name is required.');
            return;
        }
        setPhase('saving');
        setError('');
        const nextReportID = form.boundId || savedId || '';
        const body: Record<string, unknown> = {
            name: form.name.trim(),
            description: form.description,
            categories: form.categories,
            states: form.allStates ? [] : form.states,
            allStates: form.allStates,
            kind: form.kind,
            activeStatus: form.activeStatus,
            bindExisting: Boolean(form.boundId),
            confirmUnboundNewId: generated && !form.boundId && form.confirmNew,
            dataPoints: generated ? form.dataPoints.filter((row) => row.name.trim()) : [],
        };
        if (nextReportID) body.reportID = nextReportID;
        if (generated) {
            body.config = {
                dateType: form.dateType,
                fileTypes: {
                    isVisible: form.fileVisible,
                    isSelectable: form.fileSelectable,
                    types: form.fileTypes,
                    defaultType: form.defaultType,
                },
                timeSaved: { value: Number(form.timeValue) || 0, unit: form.timeUnit },
            };
        }
        try {
            const isUpdate = Boolean(docId || (mode === 'edit' && savedId));
            const row = isUpdate
                ? await updateCatalogReport({
                      id: docId || savedId,
                      ...body,
                  })
                : await createCatalogReport(body);
            setDocId(row._id ?? docId);
            setSavedId(row.reportID);
            setPacksIn(row.packsIn ?? packsIn);
            setBrandsOn(row.brandsOn ?? brandsOn);
            setJustSaved(true);
            setPhase('idle');
            onSaved({ mode, name: row.name || form.name.trim() });
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    const title =
        mode === 'create' && !saved ? REPORTS_COPY.NEW_REPORT : form.name || 'Edit report';

    return (
        <>
            <ReportsDialog
                open
                width={760}
                onClose={locked || b9 ? () => undefined : onClose}
                closeOnBackdrop={!locked && !b9}
                closeOnEscape={!locked && !b9}
            >
                <ReportsDialogHeader
                    kicker={REPORTS_COPY.REPORT_DETAILS}
                    title={title}
                    meta={
                        <div
                            className="flex items-center flex-wrap"
                            style={{ gap: 8, marginTop: 6 }}
                        >
                            {savedId ? (
                                <span
                                    style={{
                                        fontFamily: 'var(--cpm-font-mono)',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    {savedId}
                                </span>
                            ) : null}
                            <span
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-tertiary)',
                                }}
                            >
                                Report details only
                            </span>
                        </div>
                    }
                    onClose={locked || b9 ? undefined : onClose}
                />
                <ReportsDialogBody>
                    {phase === 'loading' ? (
                        <div>
                            <div style={{ paddingBottom: 18 }}>
                                <ReportsWait label="Getting this report's catalogue details. Nothing on this form is ready to read or edit yet." />
                            </div>
                            <div style={reportsGrid}>
                                <div style={span(8)}>
                                    <div
                                        className="cpm-sk"
                                        style={{ height: 40, borderRadius: 8 }}
                                    />
                                </div>
                                <div style={span(4)}>
                                    <div
                                        className="cpm-sk"
                                        style={{ height: 40, borderRadius: 8 }}
                                    />
                                </div>
                                <div style={span(12)}>
                                    <div
                                        className="cpm-sk"
                                        style={{ height: 68, borderRadius: 8 }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : null}
                    {phase === 'fetchError' ? (
                        <ReportsAlert
                            tone="danger"
                            title={`Couldn't load “${title}”`}
                            body="The details for this one report did not come back, so this form is showing nothing rather than guessing. The catalogue list behind this dialog is unaffected, and Templates and Store brands were not part of this request."
                            reason={reportsAdminErrorReason(
                                error,
                                'The details for this one report did not come back'
                            )}
                            action={
                                <CpmButton variant="outline" size="sm" onClick={() => void load()}>
                                    Retry
                                </CpmButton>
                            }
                        />
                    ) : null}
                    {phase !== 'loading' && phase !== 'fetchError' ? (
                        <div>
                            {justSaved ? (
                                <div style={{ marginBottom: 16 }}>
                                    <ReportsAlert
                                        tone="success"
                                        title={
                                            mode === 'create'
                                                ? `Saved. “${form.name.trim()}” is in the catalogue.`
                                                : `Saved. “${form.name.trim()}” was updated.`
                                        }
                                        body={
                                            mode === 'create'
                                                ? 'The catalogue row now exists, and the list behind this dialog already shows it. Nothing has been turned on for a store brand — the four assignment actions below are now available on this row.'
                                                : 'Report details was written. The row in the list behind this dialog already reflects the change, and nothing was turned on for a store brand.'
                                        }
                                    />
                                </div>
                            ) : null}
                            {error ? (
                                <div style={{ marginBottom: 16 }}>
                                    <ReportsAlert
                                        tone="danger"
                                        title={
                                            mode === 'create'
                                                ? 'Couldn’t save the new report'
                                                : 'Couldn’t save this report'
                                        }
                                        body={REPORTS_COPY.SAVE_NOTHING_WRITTEN}
                                        reason={reportsAdminErrorReason(
                                            error,
                                            REPORTS_COPY.SAVE_NOTHING_WRITTEN
                                        )}
                                        action={
                                            <CpmButton
                                                variant="outline"
                                                size="sm"
                                                onClick={() => void persist()}
                                            >
                                                {REPORTS_COPY.RETRY_THE_SAVE}
                                            </CpmButton>
                                        }
                                    />
                                </div>
                            ) : null}
                            <div style={reportsGrid}>
                                <div style={span(8)}>
                                    <ReportsFieldLabel required>Report name</ReportsFieldLabel>
                                    <ReportsInput
                                        value={form.name}
                                        disabled={locked}
                                        placeholder="What ops and merchants call this report"
                                        onChange={(e) => patchForm({ name: e.target.value })}
                                    />
                                </div>
                                <div style={span(4)}>
                                    <ReportsFieldLabel>Catalogue status</ReportsFieldLabel>
                                    <ReportsSegmented
                                        value={form.activeStatus}
                                        disabled={locked}
                                        options={[
                                            { value: 'Active', label: 'Active' },
                                            { value: 'Inactive', label: 'Inactive' },
                                        ]}
                                        onChange={(activeStatus) => patchForm({ activeStatus })}
                                    />
                                </div>
                                <div style={span(12)}>
                                    <ReportsFieldLabel hint="Shown when ops opens the report">
                                        Description
                                    </ReportsFieldLabel>
                                    <ReportsTextarea
                                        value={form.description}
                                        disabled={locked}
                                        rows={2}
                                        maxLength={240}
                                        placeholder="Optional — what the report shows, and when ops hands it out"
                                        onChange={(e) => patchForm({ description: e.target.value })}
                                    />
                                </div>
                                <div style={span(6)}>
                                    <ReportsFieldLabel>Categories</ReportsFieldLabel>
                                    <InPlaceTagToggle
                                        options={[...REPORT_CATALOG_CATEGORIES]}
                                        selected={form.categories}
                                        disabled={locked}
                                        onChange={(categories) =>
                                            patchForm({
                                                categories: categories as ReportCatalogCategory[],
                                            })
                                        }
                                    />
                                    <ReportsHelp>
                                        More than one is allowed. Only these ten exist — a state is
                                        never a category.
                                    </ReportsHelp>
                                </div>
                                <div style={span(6)}>
                                    <ReportsFieldLabel>States</ReportsFieldLabel>
                                    <CpmFilterDropdown
                                        label="States"
                                        portal
                                        disabled={locked || form.allStates}
                                        options={stateDocs
                                            .map(stateName)
                                            .filter(Boolean)
                                            .map((name) => ({ value: name, label: name }))}
                                        selected={form.states}
                                        onChange={(states) => patchForm({ states })}
                                    />
                                    <div style={{ marginTop: 9 }}>
                                        <CpmCheckbox
                                            checked={form.allStates}
                                            disabled={locked}
                                            onChange={() =>
                                                patchForm({ allStates: !form.allStates })
                                            }
                                            label="All states — this report is not tied to any one state"
                                        />
                                    </div>
                                </div>
                                <div style={span(6)}>
                                    <ReportsFieldLabel required>Kind</ReportsFieldLabel>
                                    <ReportsSegmented
                                        value={form.kind}
                                        disabled={locked}
                                        options={[
                                            {
                                                value: 'GENERATED',
                                                label: (
                                                    <>
                                                        Shop can
                                                        <br />
                                                        download
                                                    </>
                                                ),
                                            },
                                            {
                                                value: 'ALWAYS_ON',
                                                label: REPORTS_COPY.KIND_ALWAYS_ON,
                                            },
                                            { value: 'DEAD', label: REPORTS_COPY.KIND_DEAD },
                                        ]}
                                        onChange={(kind) => patchForm({ kind })}
                                    />
                                    <ReportsHelp>{KIND_HELP[form.kind]}</ReportsHelp>
                                </div>
                                <div style={span(6)}>
                                    <GeneratorBindSelect
                                        value={form.boundId}
                                        disabled={locked}
                                        confirmNew={form.confirmNew}
                                        onConfirmNew={() =>
                                            patchForm({ confirmNew: !form.confirmNew })
                                        }
                                        onChange={(boundId) => patchForm({ boundId })}
                                    />
                                </div>
                                {generated ? (
                                    <div
                                        style={{
                                            ...span(12),
                                            marginTop: 6,
                                            borderTop: '1px solid var(--cpm-border-default)',
                                            paddingTop: 16,
                                        }}
                                    >
                                        <div
                                            className="flex items-center"
                                            style={{ gap: 10, marginBottom: 4 }}
                                        >
                                            <span
                                                className="inline-flex items-center justify-center shrink-0"
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: 8,
                                                    background: 'var(--cpm-purple-tint-13)',
                                                }}
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="var(--cpm-brand-purple)"
                                                    strokeWidth="2"
                                                >
                                                    <rect
                                                        x="3"
                                                        y="4"
                                                        width="18"
                                                        height="18"
                                                        rx="2"
                                                    />
                                                    <path d="M16 2v4M8 2v4M3 10h18" />
                                                </svg>
                                            </span>
                                            <div>
                                                <div
                                                    style={{
                                                        fontSize: 13.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                    }}
                                                >
                                                    {REPORTS_COPY.WHAT_THEY_PICK}
                                                </div>
                                                <div
                                                    style={{
                                                        marginTop: 2,
                                                        fontSize: 11.5,
                                                        fontWeight: 600,
                                                        color: 'var(--cpm-text-secondary)',
                                                    }}
                                                >
                                                    What a merchant sees on Generate Report once a
                                                    store brand has this on. Only Shop can download
                                                    reports need it.
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ ...reportsGrid, marginTop: 12 }}>
                                            <div style={span(6)}>
                                                <ReportsFieldLabel
                                                    trailing={
                                                        <InfoI
                                                            title={REPORTS_TIPS.DATE_TYPE.title}
                                                            body={REPORTS_TIPS.DATE_TYPE.body}
                                                        />
                                                    }
                                                >
                                                    Date
                                                </ReportsFieldLabel>
                                                <ReportsSegmented
                                                    value={form.dateType}
                                                    disabled={locked}
                                                    options={[
                                                        {
                                                            value: 'Single Date',
                                                            label: 'Single date',
                                                        },
                                                        {
                                                            value: 'Range Date',
                                                            label: 'Date range',
                                                        },
                                                    ]}
                                                    onChange={(dateType) => patchForm({ dateType })}
                                                />
                                                <ReportsHelp>
                                                    This is what shops get when they download.
                                                </ReportsHelp>
                                            </div>
                                            <div style={span(6)}>
                                                <ReportsFieldLabel
                                                    trailing={
                                                        <InfoI
                                                            title={REPORTS_TIPS.FILE_TYPE.title}
                                                            body={REPORTS_TIPS.FILE_TYPE.body}
                                                        />
                                                    }
                                                >
                                                    File type
                                                </ReportsFieldLabel>
                                                <div
                                                    className="flex flex-col"
                                                    style={{
                                                        gap: 12,
                                                        padding: '12px 14px',
                                                        border: '1px solid var(--cpm-border-default)',
                                                        borderRadius: 10,
                                                        background: 'var(--cpm-surface-subtle)',
                                                    }}
                                                >
                                                    <div
                                                        className="flex flex-col"
                                                        style={{ gap: 7 }}
                                                    >
                                                        <CpmCheckbox
                                                            checked={form.fileVisible}
                                                            disabled={locked}
                                                            onChange={() =>
                                                                setForm((prev) => ({
                                                                    ...prev,
                                                                    fileVisible: !prev.fileVisible,
                                                                }))
                                                            }
                                                            label="Shops see the Excel / PDF radios"
                                                        />
                                                        <CpmCheckbox
                                                            checked={form.fileSelectable}
                                                            disabled={locked}
                                                            onChange={() =>
                                                                setForm((prev) => ({
                                                                    ...prev,
                                                                    fileSelectable:
                                                                        !prev.fileSelectable,
                                                                }))
                                                            }
                                                            label="Shops can change it"
                                                        />
                                                    </div>
                                                    <div
                                                        style={{
                                                            borderTop:
                                                                '1px solid var(--cpm-border-default)',
                                                            paddingTop: 11,
                                                        }}
                                                    >
                                                        <div
                                                            className="uppercase"
                                                            style={{
                                                                fontSize: 9.5,
                                                                fontWeight: 800,
                                                                letterSpacing: '0.08em',
                                                                color: 'var(--cpm-text-tertiary)',
                                                            }}
                                                        >
                                                            Formats that exist
                                                        </div>
                                                        <div
                                                            className="flex items-center"
                                                            style={{ gap: 18, marginTop: 8 }}
                                                        >
                                                            {(
                                                                ['Excel', 'PDF'] as ReportFileType[]
                                                            ).map((type) => (
                                                                <CpmCheckbox
                                                                    key={type}
                                                                    checked={form.fileTypes.includes(
                                                                        type
                                                                    )}
                                                                    disabled={locked}
                                                                    label={type}
                                                                    onChange={() => {
                                                                        setForm((prev) => {
                                                                            const next =
                                                                                prev.fileTypes.includes(
                                                                                    type
                                                                                )
                                                                                    ? prev.fileTypes.filter(
                                                                                          (item) =>
                                                                                              item !==
                                                                                              type
                                                                                      )
                                                                                    : [
                                                                                          ...prev.fileTypes,
                                                                                          type,
                                                                                      ];
                                                                            return {
                                                                                ...prev,
                                                                                fileTypes: next,
                                                                                defaultType:
                                                                                    next.includes(
                                                                                        prev.defaultType
                                                                                    )
                                                                                        ? prev.defaultType
                                                                                        : (next[0] ??
                                                                                          'Excel'),
                                                                            };
                                                                        });
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div
                                                        style={{
                                                            borderTop:
                                                                '1px solid var(--cpm-border-default)',
                                                            paddingTop: 11,
                                                        }}
                                                    >
                                                        <div
                                                            className="uppercase"
                                                            style={{
                                                                fontSize: 9.5,
                                                                fontWeight: 800,
                                                                letterSpacing: '0.08em',
                                                                color: 'var(--cpm-text-tertiary)',
                                                            }}
                                                        >
                                                            Pre-selected
                                                        </div>
                                                        <div style={{ marginTop: 8 }}>
                                                            {form.fileTypes.length ? (
                                                                <ReportsSegmented
                                                                    value={form.defaultType}
                                                                    disabled={locked}
                                                                    options={form.fileTypes.map(
                                                                        (type) => ({
                                                                            value: type,
                                                                            label: type,
                                                                        })
                                                                    )}
                                                                    onChange={(defaultType) =>
                                                                        patchForm({ defaultType })
                                                                    }
                                                                />
                                                            ) : (
                                                                <p
                                                                    className="m-0"
                                                                    style={{
                                                                        fontSize: 11.5,
                                                                        fontWeight: 700,
                                                                        color: 'var(--cpm-warning-text-med)',
                                                                    }}
                                                                >
                                                                    No format exists on this report,
                                                                    so there is nothing to
                                                                    pre-select.
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    ...span(12),
                                                    borderTop:
                                                        '1px solid var(--cpm-border-default)',
                                                    paddingTop: 14,
                                                }}
                                            >
                                                <ReportsFieldLabel
                                                    trailing={
                                                        <InfoI
                                                            title={REPORTS_TIPS.DATA_POINTS.title}
                                                            body={REPORTS_TIPS.DATA_POINTS.body}
                                                        />
                                                    }
                                                    end={
                                                        form.dataPoints.length === 1
                                                            ? '1 data point'
                                                            : `${form.dataPoints.length} data points`
                                                    }
                                                >
                                                    Data points
                                                </ReportsFieldLabel>
                                                {form.dataPoints.length === 0 ? (
                                                    <p
                                                        className="m-0"
                                                        style={{
                                                            fontSize: 11.5,
                                                            fontWeight: 700,
                                                            color: 'var(--cpm-text-secondary)',
                                                        }}
                                                    >
                                                        0 data points. That is a saved number, not a
                                                        gap — shops get no table on generate.
                                                    </p>
                                                ) : null}
                                                {form.dataPoints.map((row, index) => (
                                                    <div
                                                        key={index}
                                                        className="grid items-center"
                                                        style={{
                                                            gridTemplateColumns:
                                                                '22px minmax(0, 1fr) minmax(0, 1.5fr) 32px',
                                                            gap: 10,
                                                            padding: '4px 0',
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                fontSize: 11.5,
                                                                fontWeight: 800,
                                                                color: 'var(--cpm-text-tertiary)',
                                                                fontVariantNumeric: 'tabular-nums',
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </span>
                                                        <ReportsInput
                                                            compact
                                                            value={row.name}
                                                            disabled={locked}
                                                            placeholder="What shops see in the list"
                                                            onChange={(e) => {
                                                                const name = e.target.value;
                                                                setForm((prev) => {
                                                                    const next = [
                                                                        ...prev.dataPoints,
                                                                    ];
                                                                    next[index] = {
                                                                        ...next[index],
                                                                        name,
                                                                    };
                                                                    return {
                                                                        ...prev,
                                                                        dataPoints: next,
                                                                    };
                                                                });
                                                            }}
                                                        />
                                                        <ReportsInput
                                                            compact
                                                            value={row.description ?? ''}
                                                            disabled={locked}
                                                            placeholder="Optional — what this row means"
                                                            onChange={(e) => {
                                                                const description = e.target.value;
                                                                setForm((prev) => {
                                                                    const next = [
                                                                        ...prev.dataPoints,
                                                                    ];
                                                                    next[index] = {
                                                                        ...next[index],
                                                                        description,
                                                                    };
                                                                    return {
                                                                        ...prev,
                                                                        dataPoints: next,
                                                                    };
                                                                });
                                                            }}
                                                        />
                                                        <CpmIconButton
                                                            label="Remove this data point"
                                                            size="md"
                                                            tone="danger"
                                                            disabled={locked}
                                                            icon={
                                                                <svg
                                                                    width="13"
                                                                    height="13"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                >
                                                                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                                                                </svg>
                                                            }
                                                            onClick={() =>
                                                                setForm((prev) => ({
                                                                    ...prev,
                                                                    dataPoints:
                                                                        prev.dataPoints.filter(
                                                                            (_, i) => i !== index
                                                                        ),
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                ))}
                                                <div
                                                    className="flex items-center"
                                                    style={{ gap: 12, marginTop: 10 }}
                                                >
                                                    <CpmButton
                                                        variant="outline"
                                                        size="sm"
                                                        disabled={locked}
                                                        style={{
                                                            height: 36,
                                                            paddingLeft: 16,
                                                            paddingRight: 16,
                                                            fontSize: 13,
                                                            color: 'var(--cpm-text-primary)',
                                                            flex: 'none',
                                                        }}
                                                        onClick={() =>
                                                            setForm((prev) => ({
                                                                ...prev,
                                                                dataPoints: [
                                                                    ...prev.dataPoints,
                                                                    { name: '', description: '' },
                                                                ],
                                                            }))
                                                        }
                                                    >
                                                        Add a data point
                                                    </CpmButton>
                                                    <span
                                                        style={{
                                                            fontSize: 11.5,
                                                            fontWeight: 600,
                                                            lineHeight: 1.45,
                                                            color: 'var(--cpm-text-secondary)',
                                                        }}
                                                    >
                                                        Shops see the names as a read-only list.
                                                        They cannot uncheck a row or drag it.
                                                    </span>
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    ...span(6),
                                                    borderTop:
                                                        '1px solid var(--cpm-border-default)',
                                                    paddingTop: 14,
                                                }}
                                            >
                                                <ReportsFieldLabel
                                                    trailing={
                                                        <InfoI
                                                            title={REPORTS_TIPS.TIME_SAVED.title}
                                                            body={REPORTS_TIPS.TIME_SAVED.body}
                                                        />
                                                    }
                                                >
                                                    Time saved
                                                </ReportsFieldLabel>
                                                <div
                                                    className="flex items-center"
                                                    style={{ gap: 10 }}
                                                >
                                                    <div style={{ width: 92 }}>
                                                        <ReportsInput
                                                            compact
                                                            type="number"
                                                            value={form.timeValue}
                                                            disabled={locked}
                                                            onChange={(e) =>
                                                                patchForm({
                                                                    timeValue:
                                                                        Number(e.target.value) || 0,
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                    <ReportsSegmented
                                                        size="sm"
                                                        value={
                                                            form.timeUnit === 'hours' ||
                                                            form.timeUnit === 'hour'
                                                                ? 'hours'
                                                                : 'minutes'
                                                        }
                                                        disabled={locked}
                                                        options={[
                                                            { value: 'minutes', label: 'Minutes' },
                                                            { value: 'hours', label: 'Hours' },
                                                        ]}
                                                        onChange={(timeUnit) =>
                                                            patchForm({
                                                                timeUnit: timeUnit as SavedTimeUnit,
                                                            })
                                                        }
                                                    />
                                                </div>
                                                <ReportsHelp>
                                                    History reads “Saved {form.timeValue}{' '}
                                                    {form.timeUnit} of your day”. Not a control on
                                                    generate.
                                                </ReportsHelp>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                <div
                                    style={{
                                        ...span(12),
                                        marginTop: 6,
                                        border: '1px solid var(--cpm-border-default)',
                                        borderRadius: 10,
                                        background: 'var(--cpm-surface-subtle)',
                                        padding: '16px 18px',
                                    }}
                                >
                                    <div
                                        className="flex items-start justify-between flex-wrap"
                                        style={{ gap: 18 }}
                                    >
                                        <div style={{ maxWidth: 430 }}>
                                            <div
                                                className="uppercase"
                                                style={{
                                                    fontSize: 9.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.08em',
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                Templates and store brands
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 6,
                                                    fontSize: 13.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.01em',
                                                }}
                                            >
                                                Assignment for this one report
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    lineHeight: 1.5,
                                                    color: 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                Two separate lists. Putting this report in a
                                                template turns it on for nobody; turning it on for a
                                                store brand puts it in no template.
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 13.5, fontWeight: 800 }}>
                                                In {packsIn} templates
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 3,
                                                    fontSize: 13.5,
                                                    fontWeight: 800,
                                                }}
                                            >
                                                On for {brandsOn} store brands
                                            </div>
                                        </div>
                                    </div>
                                    {!saved ? (
                                        <div style={{ marginTop: 14 }}>
                                            <ReportsAlert
                                                tone="warning"
                                                title="Save this report before assigning it"
                                                body="There is no catalogue row yet, so there is nothing to put in a template or turn on for a store brand. Both actions start working once this row is saved."
                                            />
                                        </div>
                                    ) : null}
                                    <div
                                        className="flex flex-wrap items-center"
                                        style={{ gap: 8, marginTop: 14 }}
                                    >
                                        <CpmButton
                                            variant="outline"
                                            size="sm"
                                            disabled={!canAssign || locked}
                                            onClick={() => setB9('templates')}
                                        >
                                            Which templates include this report
                                        </CpmButton>
                                        <CpmButton
                                            variant="outline"
                                            size="sm"
                                            disabled={!canAssign || locked}
                                            onClick={() => setB9('brands')}
                                        >
                                            On for which store brands
                                        </CpmButton>
                                    </div>
                                    {kindLocked && saved ? (
                                        <div
                                            style={{
                                                marginTop: 12,
                                                padding: '11px 13px',
                                                border: '1px solid var(--cpm-info-border)',
                                                borderRadius: 8,
                                                background: 'var(--cpm-info-bg)',
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                lineHeight: 1.45,
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            Always on and Doesn’t run yet cannot use these actions.
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </ReportsDialogBody>
                <ReportsDialogFooter
                    note="Catalogue details only. Assignment is a separate write."
                    saving={locked}
                    savingLabel="Saving this report's catalogue details."
                    onCancel={onClose}
                    primaryLabel={REPORTS_COPY.SAVE}
                    onPrimary={() => void persist()}
                    primaryDisabled={
                        phase === 'loading' || phase === 'fetchError' || !form.name.trim()
                    }
                />
            </ReportsDialog>
            {b9 === 'templates' && savedId ? (
                <TemplatesForReportModal
                    reportID={savedId}
                    onClose={() => setB9(null)}
                    onSaved={(n) => setPacksIn(n)}
                />
            ) : null}
            {b9 === 'brands' && savedId ? (
                <StoreBrandsForReportModal
                    reportID={savedId}
                    onClose={() => setB9(null)}
                    onSaved={(n) => {
                        setBrandsOn(n);
                        onSaved({ mode, name: form.name.trim() });
                    }}
                />
            ) : null}
        </>
    );
}
